// A suggested spread for the overdue backlog.
//
// A sizeable part of the recall list is 18 months to 2+ years overdue.
// Listing them all as due this month would flood the office; the practice
// wants them spread across months, and wants a person to decide - the visit
// dates behind the list come from an import nobody has checked yet. So this
// only proposes. Nothing here writes anything; the calendar shows the
// proposal, lets staff edit any row, and applies it on a click.
//
// The rule is deliberately simple enough to explain in a sentence: most
// overdue first, fill the month on screen to the cap, then the next month,
// for as many months as asked; whoever is left is named as overflow rather
// than quietly dropped. Ties break on id so the same list always gives the
// same plan.

export interface SpreadPatient {
  patientId: string;
  monthsSince: number | null | undefined;
  /** When given, a patient with no digits here is left out: nobody to text. */
  patientPhone?: string | null;
  /** When given, an import placeholder or blank name is left out: "Hi Patient". */
  patientName?: string | null;
  /** Already planned for fromMonth by an earlier run: stays, and takes a place. */
  plannedForThisMonth?: boolean;
}

export interface SpreadRequest {
  patients: SpreadPatient[];
  /** The month on screen, YYYY-MM. Filling starts here. */
  fromMonth: string;
  /** How many months to spread over, counting fromMonth. */
  months: number;
  capPerMonth: number;
  /** Only patients at least this overdue are part of the backlog. */
  minMonthsSince?: number;
  /** Places already taken in later months by earlier runs, per YYYY-MM. */
  alreadyPlanned?: Record<string, number>;
}

export interface SpreadResult {
  assignments: Record<string, string>;
  overflow: string[];
  /** Planned for fromMonth before this run; untouched, counted against its cap. */
  kept: string[];
  /** Not placed, and why. Found in QA: 7 of 61 had no phone, 11 no usable name. */
  leftOut: { noPhone: string[]; noName: string[] };
}

// The import writes "Patient entity_2FKVEQBTQKG00" where it has no name, and
// the list writes "Unknown patient" where it has nothing at all. Neither can
// open a text message.
export function isUsableName(name: string | null | undefined): boolean {
  const n = String(name || '').trim();
  // A phone number standing where a name should be is not a name either:
  // "Hi (202)" is not a greeting. A name has a letter in it.
  return n.length > 0 && !/^patient entity_/i.test(n) && !/^unknown( patient)?$/i.test(n) && /\p{L}/u.test(n);
}
const hasPhone = (phone: string | null | undefined): boolean => String(phone || '').replace(/\D/g, '').length > 0;

// String arithmetic on YYYY-MM: no Date, so no timezone can move a month.
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  const year = Math.floor(total / 12);
  const mon = (total % 12) + 1;
  return `${year}-${String(mon).padStart(2, '0')}`;
}

export function suggestSpread({
  patients, fromMonth, months, capPerMonth, minMonthsSince = 18, alreadyPlanned = {},
}: SpreadRequest): SpreadResult {
  const cap = Math.max(0, Math.floor(Number(capPerMonth) || 0));
  const span = Math.max(0, Math.floor(Number(months) || 0));
  const eligible = (patients || [])
    .filter((p) => p && p.patientId && Number.isFinite(Number(p.monthsSince)) && p.monthsSince != null && Number(p.monthsSince) >= minMonthsSince);

  // Who cannot be placed, and who is already placed. A missing phone is
  // reported first: a patient with neither is one problem to fix, not two.
  const leftOut = { noPhone: [] as string[], noName: [] as string[] };
  const kept: string[] = [];
  const backlog = eligible.filter((p) => {
    if (p.patientPhone !== undefined && !hasPhone(p.patientPhone)) { leftOut.noPhone.push(p.patientId); return false; }
    if (p.patientName !== undefined && !isUsableName(p.patientName)) { leftOut.noName.push(p.patientId); return false; }
    if (p.plannedForThisMonth) { kept.push(p.patientId); return false; }
    return true;
  }).sort((a, b) => Number(b.monthsSince) - Number(a.monthsSince) || (a.patientId < b.patientId ? -1 : a.patientId > b.patientId ? 1 : 0));

  // Places left in each month of the window: the cap, less what earlier runs
  // put there - the kept rows for this month, the counts passed for later ones.
  const free: number[] = [];
  for (let i = 0; i < span; i += 1) {
    const month = addMonths(fromMonth, i);
    const taken = i === 0 ? kept.length : Math.max(0, Math.floor(Number(alreadyPlanned[month]) || 0));
    free.push(Math.max(0, cap - taken));
  }

  const assignments: Record<string, string> = {};
  const overflow: string[] = [];
  let slot = 0;
  for (const p of backlog) {
    while (slot < span && free[slot] <= 0) slot += 1;
    if (slot >= span) { overflow.push(p.patientId); continue; }
    assignments[p.patientId] = addMonths(fromMonth, slot);
    free[slot] -= 1;
  }
  return { assignments, overflow, kept, leftOut };
}
